import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../components/AuthContext';
import InlineMessage from '../components/InlineMessage';
import { readInviteToken } from '../lib/inviteToken';

export default function SetPasswordPage() {
  // Captured synchronously on first render, straight from the URL as delivered —
  // the same signal Supabase uses internally to decide whether to fire
  // PASSWORD_RECOVERY, read directly here instead of racing to catch that
  // one-shot event before AuthContext's own listener consumes it.
  const [urlMarker] = useState(() => {
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    return hash.includes('type=recovery') || search.includes('type=recovery');
  });

  // #373, Session 78g. The URL marker alone was the gate here, and it is not
  // sufficient: RecoveryGate in App.js redirects to this page after catching
  // PASSWORD_RECOVERY, and that redirect necessarily arrives with a clean URL
  // carrying no marker at all. Reading the marker only would then show "Link
  // expired" to someone holding a perfectly valid recovery session.
  //
  // AuthContext carries the flag because it survives the redirect and the race
  // this component's original comment describes. The marker is kept as well, so
  // a link that lands here directly still works even if the event was missed.
  const { passwordRecovery, clearPasswordRecovery, recoveryUserId, noteRecoveryUser } = useAuth();
  const isRecoveryLink = urlMarker || passwordRecovery;

  // #495, Session 79. A separate state from `validSession`, because "the link
  // is spent" and "the link is for somebody else" need different words on
  // screen — the second one must never read as a normal expiry, since the
  // person seeing it may be about to overwrite a colleague's credentials.
  const [wrongAccount, setWrongAccount] = useState(false);

  // #547, Session 79.14f, gate decisions D1 and D2.
  //
  // An invite now arrives as a one-time CODE in the fragment rather than as an
  // already-redeemed session, and NOTHING is redeemed until the person presses
  // a button. Read synchronously on first render for the same reason the marker
  // above is: supabase-js clears the fragment during its own startup.
  const [invite, setInvite] = useState(() => readInviteToken(window.location.hash));
  const [redeeming, setRedeeming] = useState(false);
  const [redeemFailed, setRedeemFailed] = useState(false);

  const [checking, setChecking] = useState(true);
  const [validSession, setValidSession] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // #547 D2 — an invite code that has not been redeemed yet establishes NO
    // session, by design. Asking for one here would find nothing and render
    // "Link expired" at the exact moment the link is still perfectly good.
    // Cleared by redeemInvite() below, which puts this effect back in charge.
    if (invite) {
      setChecking(false);
      return;
    }

    if (!isRecoveryLink) {
      // No recovery marker in the URL at all — don't even ask Supabase.
      // A currently-logged-in user browsing to /set-password shouldn't
      // land on the password form.
      setValidSession(false);
      setChecking(false);
      return;
    }

    let cancelled = false;
    setChecking(true);
    setCheckFailed(false);

    supabase.auth.getSession()
      .then(({ data }) => {
        if (cancelled) return;
        // type=recovery was present, but confirm Supabase actually established
        // a session from it — an expired or already-used token fails this
        // even though the URL marker was there.
        const session = data?.session || null;

        // #495 — AND confirm the session belongs to the account this link was
        // ISSUED FOR. Checking only that a session exists is what allowed a
        // reset for one person to change another person's password: a spent
        // token establishes nothing, so `getSession()` returns whatever was
        // already open in the browser, and `updateUser()` writes to that.
        //
        // FAILS CLOSED. If the intended recipient could not be determined at
        // all, the form is refused rather than shown. That can turn a genuine
        // reset into "request another link", which is recoverable in seconds —
        // whereas the other direction silently hands somebody a colleague's
        // account, which is not recoverable at all and leaves no trace.
        const intended = recoveryUserId;
        const belongsToIntended = !!session && !!intended && session.user?.id === intended;

        setWrongAccount(!!session && !belongsToIntended);
        setValidSession(belongsToIntended);
        setChecking(false);
      })
      .catch(() => {
        // #96 — getSession() rejected. Surface it instead of an indefinite spinner.
        if (cancelled) return;
        setCheckFailed(true);
        setChecking(false);
      });

    return () => { cancelled = true; };
    // #495 — `recoveryUserId` joins the deps because it can arrive AFTER the
    // first run: the PKCE shape learns it from the PASSWORD_RECOVERY event
    // rather than from the URL. Without it here, a legitimate reset would be
    // judged against a null identity and refused.
  }, [isRecoveryLink, retryKey, recoveryUserId, invite]);

  // #547 D2 — the press that spends the code. Nothing above this line touches
  // it, which is the entire point: a machine that fetches the page never gets
  // here, and a machine that somehow loads it still cannot press.
  async function redeemInvite() {
    setRedeeming(true);
    const { data, error: verifyError } = await supabase.auth.verifyOtp({
      token_hash: invite.tokenHash,
      type: invite.type,
    });
    setRedeeming(false);

    if (verifyError || !data?.user?.id) {
      // The code really was spent or has timed out. D7 — one message for one
      // situation, the same words Login shows, rather than a second dialect.
      setRedeemFailed(true);
      return;
    }

    // #495's guard compares the session against the account the link was FOR,
    // and neither of its usual sources can see an invite redeemed this way.
    // See noteRecoveryUser() in AuthContext for why this id is better evidence
    // than the one it stands in for.
    noteRecoveryUser(data.user.id);

    // The code is spent now, so leaving it in the address would turn a refresh
    // into a false "link expired". The session is what carries the flow from
    // here.
    window.history.replaceState(null, '', window.location.pathname);
    setInvite(null);
    setChecking(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setSaving(true);

    // #495 — re-checked HERE, not only at mount. The gate above runs once when
    // the page loads; a session can change after it and before this button is
    // pressed — another tab signs in, a token refresh resolves, the person
    // leaves the form open. `updateUser()` always writes to whatever the
    // session is AT THE MOMENT OF THE CALL, so the check has to sit against the
    // call rather than somewhere upstream of it.
    //
    // Cheap, and the thing it prevents is a silent credential change on
    // somebody else's account.
    const { data: nowData } = await supabase.auth.getSession();
    const nowId = nowData?.session?.user?.id;
    if (!nowId || !recoveryUserId || nowId !== recoveryUserId) {
      setSaving(false);
      setValidSession(false);
      setWrongAccount(!!nowId);
      return;
    }

    const { error: updateError } = await supabase.auth.updateUser({ password });
    setSaving(false);
    if (updateError) { setError(updateError.message); return; }
    // #373 — released only once the password actually saved. Clearing it any
    // earlier, or on navigation, would let the requirement be dismissed by
    // leaving the screen, which is the state that produced a passwordless
    // account in the first place.
    clearPasswordRecovery();
    setDone(true);
    setTimeout(() => { window.location.href = '/'; }, 1200);
  }

  const wrap = { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--off-white)' };

  // #547 D7 — the code was already spent. Deliberately the SAME sentence Login
  // shows for the same situation, because two dialects for one problem is how
  // people stop reading either.
  if (redeemFailed) {
    return (
      <div style={wrap}>
        <div className="card" style={{ maxWidth: 420, padding: '2rem', textAlign: 'center' }}>
          <div className="page-title">Link already used</div>
          <p className="text-muted" style={{ marginTop: 8 }}>
            That link has already been used, or it has expired. These links only work once.
            Use "Forgot password?" on the sign-in screen to send yourself a new one, or ask
            your administrator to resend your invite.
          </p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => { window.location.href = '/'; }}>
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  // #547 D2 and D4 — the press, and NOTHING ELSE ON THE SCREEN.
  //
  // D4 IS BUILT NARROWER THAN IT WAS DRAWN, and the reason is structural rather
  // than a shortcut. The gate drew this greeting the person by name; the link
  // carries a one-time code and nothing else, so the name is not known until
  // AFTER the code is redeemed — which is the thing this screen exists to defer.
  // Putting the name in the address to get it back would write a real person's
  // name into browser history for a greeting.
  //
  // THE MINIMUM D4 TOUCHES IS THE 2-TAPS-UNDER-3-SECONDS STANDARD, and it still
  // holds: one press, full width, no form to read first. The password fields are
  // deliberately absent so nobody types a password into a form about to refuse it.
  if (invite) {
    return (
      <div style={wrap}>
        <div className="card" style={{ maxWidth: 380, width: '100%', padding: '2rem', textAlign: 'center' }}>
          <div className="page-title">Stede</div>
          <div className="text-muted" style={{ fontSize: 12 }}>every tool · every part · everywhere</div>
          <div className="page-title" style={{ fontSize: 18, marginTop: 20, marginBottom: 4 }}>Welcome to Stede</div>
          <p className="text-muted" style={{ fontSize: 14, marginBottom: '1.5rem' }}>
            Press continue to choose your password. It takes about a minute.
          </p>
          <button
            className="btn btn-primary"
            onClick={redeemInvite}
            disabled={redeeming}
            style={{ width: '100%', padding: '14px' }}
          >
            {redeeming ? 'One moment...' : 'Continue'}
          </button>
        </div>
      </div>
    );
  }

  if (checking) {
    return <div style={wrap}><div className="flex-center gap-1"><div className="spinner" /><span style={{ color: 'var(--gray-lt)' }}>Loading...</span></div></div>;
  }

  if (checkFailed) {
    return (
      <div style={wrap}>
        <div className="card" style={{ maxWidth: 380, padding: '2rem', textAlign: 'center' }}>
          <div className="page-title">Something went wrong</div>
          <p className="text-muted" style={{ marginTop: 8 }}>
            We couldn't verify your link. Check your connection and try again.
          </p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setRetryKey(k => k + 1)}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // #495 — said separately from "Link expired" on purpose. Somebody else is
  // signed in on this device, and naming that is what stops them requesting
  // link after link while every one fails the same way for a reason no message
  // names.
  //
  // #547, Session 79.14e — the DIAGNOSIS was right and the INSTRUCTION under it
  // was wrong; see the corrected copy below. Reaching this screen means the
  // token was already redeemed, so the link in their mail is now spent whatever
  // they do next.
  // #548, Session 79.14f — DONE IS CHECKED FIRST, and the order is the fix.
  //
  // WHAT PEOPLE SAW, reported by Colby from a real invite: a "token expired"
  // message flashing for a moment after pressing Set password, just before the
  // home page opened. It never blocked anyone, and it is almost certainly what
  // Alex Artibee reported as an expired link — his account shows him signed in
  // fifteen minutes after it was created.
  //
  // WHY IT HAPPENED, and it was nobody's carelessness. On success this page
  // calls clearPasswordRecovery(), which nulls the id of the account the link
  // was issued for. That id is a dependency of the guard effect above, so the
  // effect re-runs, finds a live session and no expected identity, and
  // correctly concludes they do not match. Both failure screens sat ABOVE this
  // one, so one of them won the race for the 1.2 seconds before the redirect.
  //
  // Pre-existing since #495, Session 79 — the guard and the clearing shipped
  // together and neither is wrong. ONCE THE PASSWORD IS ACTUALLY SAVED, NO
  // FAILURE SCREEN IS TRUE ANY MORE, so this one outranks them.
  if (done) {
    return (
      <div style={wrap}>
        <div className="card" style={{ maxWidth: 380, padding: '2rem', textAlign: 'center' }}>
          <div className="page-title">Password set</div>
          <p className="text-muted" style={{ marginTop: 8 }}>Signing you in...</p>
        </div>
      </div>
    );
  }

  if (wrongAccount) {
    return (
      <div style={wrap}>
        <div className="card" style={{ maxWidth: 420, padding: '2rem', textAlign: 'center' }}>
          <div className="page-title">Someone else is signed in</div>
          <p className="text-muted" style={{ marginTop: 8 }}>
            This link is for a different account than the one signed in on this device,
            so we have not changed any password.
          </p>
          {/* #547, Session 79.14e — CORRECTED. The old wording was advice that
              could not work: it said "sign out first, then open the link again",
              but OPENING IT IS WHAT SPENT IT. A Supabase recovery token is
              single-use, so the second open comes back otp_expired.
              PROVEN by following the instruction exactly on staging — sign out,
              reopen the same link, and it lands on a bare sign-in screen with
              the error only in the URL fragment. The remedy is a NEW link. */}
          <p className="text-muted" style={{ marginTop: 8 }}>
            This link has now been used up — they only work once — so opening it again
            will not help. Sign out, then ask your administrator for a new invite, or use
            "Forgot password?" on the sign-in screen.
          </p>
          <button
            className="btn btn-primary"
            style={{ marginTop: 16 }}
            onClick={async () => { await supabase.auth.signOut(); window.location.href = '/'; }}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  if (!validSession) {
    return (
      <div style={wrap}>
        <div className="card" style={{ maxWidth: 380, padding: '2rem', textAlign: 'center' }}>
          <div className="page-title">Link expired</div>
          <p className="text-muted" style={{ marginTop: 8 }}>
            This password setup link is invalid or has expired. Ask your admin to resend your invite.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <form onSubmit={handleSubmit} className="card" style={{ maxWidth: 380, width: '100%', padding: '2rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div className="page-title">Stede</div>
          <div className="text-muted" style={{ fontSize: 12 }}>every tool · every part · everywhere</div>
        </div>
        <div className="page-title" style={{ fontSize: 18, marginBottom: 4 }}>Set your password</div>
        <p className="text-muted" style={{ fontSize: 14, marginBottom: '1.5rem' }}>
          Create a password to finish setting up your account.
        </p>

        {error && <InlineMessage type="error" message={error} />}

        <div className="form-group">
          <label className="form-label">New password</label>
          <input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters" autoFocus />
        </div>
        <div className="form-group">
          <label className="form-label">Confirm password</label>
          <input className="form-input" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Re-enter password" />
        </div>

        <button className="btn btn-primary" type="submit" disabled={saving} style={{ width: '100%', marginTop: '0.5rem' }}>
          {saving ? 'Saving...' : 'Set password'}
        </button>
      </form>
    </div>
  );
}